import {
  Document,
  Font,
  Image,
  Page,
  Path,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import {
  ActivityAdvice,
  ActivityMateriel,
  i18nKey,
} from "domain/entities/activities.entities";
import { Seance } from "domain/entities/seances.entities";
import { Technique } from "domain/entities/techniques.entities";
import removeMarkdown from "markdown-to-text";
import { Trans } from "react-i18next";
import grey300 from "ui/assets/images/seance-pdf-banners/grey-300.png";
import lightGreen500 from "ui/assets/images/seance-pdf-banners/light-green-500.png";
import paleGreen200 from "ui/assets/images/seance-pdf-banners/pale-green-200.png";
import paleGreen400 from "ui/assets/images/seance-pdf-banners/pale-green-400.png";
import pink200 from "ui/assets/images/seance-pdf-banners/pink-200.png";
import pink300 from "ui/assets/images/seance-pdf-banners/pink-300.png";
import pink400 from "ui/assets/images/seance-pdf-banners/pink-400.png";
import pink500 from "ui/assets/images/seance-pdf-banners/pink-500.png";
import { translate } from "ui/i18n";
import {
  getDurationInHours,
  getTotalSeanceDuration,
} from "ui/utils/formatters";

interface IProps {
  seance: Seance;
  advice: ActivityAdvice;
  materiels: ActivityMateriel[];
  userName: string;
  startTimes?: string[];
}

export const SeancePDF = ({
  seance,
  advice,
  materiels,
  userName,
  startTimes,
}: IProps) => {
  const { image, bannerColor } = getBannerStyle(getTotalSeanceDuration(seance));

  Font.register({
    family: "Plus Jakarta Sans",
    fonts: [
      {
        src: require("ui/fonts/PlusJakartaSans-VariableFont_wght.ttf"), //eslint-disable-line
        fontWeight: 400,
      },
      {
        src: require("ui/fonts/PlusJakartaSans-SemiBold.ttf"), //eslint-disable-line
        fontWeight: 500,
      },
      {
        src: require("ui/fonts/PlusJakartaSans-Bold.ttf"), //eslint-disable-line
        fontWeight: 700,
      },
    ],
  });

  return (
    <Document
      title={seance.title}
      style={{ fontFamily: "Plus Jakarta Sans", fontWeight: 700 }}
    >
      <Page size="A4" style={{ paddingHorizontal: 20, paddingVertical: 30 }}>
        <View style={{ width: "100%" }}>
          <Image src={image} style={{ width: "100%", borderRadius: "10px" }} />
          <Text
            style={{
              position: "absolute",
              color: bannerColor,
              fontSize: "18px",
              padding: "14px",
              bottom: 0,
              left: 0,
            }}
          >
            {seance.title}
          </Text>
        </View>
        <View
          style={{
            marginVertical: 10,
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {seance?.userId && (
            <View
              style={{
                marginRight: 5,
                height: 22,
                width: 22,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#485585",
                borderRadius: "100%",
              }}
            >
              <Text
                style={{
                  color: "white",
                  fontSize: 10,
                  fontWeight: 500,
                }}
              >
                {userName?.charAt(0)}
              </Text>
            </View>
          )}

          <InfoPill variant="primary">
            {getDurationInHours(getTotalSeanceDuration(seance))}
          </InfoPill>
          <InfoPill>
            {seance.onsite &&
              seance.remote &&
              translate("ns1:Activities.remote&onsite")}
            {seance.onsite &&
              !seance.remote &&
              translate("ns1:Activities.onsite")}
            {!seance.onsite &&
              seance.remote &&
              translate("ns1:Activities.remote")}
          </InfoPill>
          {seance.participants ? (
            <InfoPill>
              <Trans
                i18nKey={"Activities.UntilParticipants" as i18nKey}
                values={{ participants: seance.participants }}
              />
            </InfoPill>
          ) : (
            <></>
          )}
          {seance.participants === 0 && (
            <InfoPill>
              <Trans
                i18nKey={"Activities.MoreThanParticipants" as i18nKey}
                values={{ participants: 20 }}
              />
            </InfoPill>
          )}
        </View>

        <Text
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "#485585",
            marginTop: 20,
          }}
        >
          {`${translate("ns1:Activities.Goal")}: ${seance.objectif}`}
        </Text>

        <Text
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "#485585",
            marginTop: 20,
          }}
        >
          {translate("ns1:Activities.ActivitySteps")}
        </Text>

        {seance.techniques.map((technique, index) => (
          <TechniqueComponent
            technique={technique}
            key={index}
            index={index}
            techniquesLength={seance.techniques?.length || 0}
            startTime={startTimes ? startTimes[index] : undefined}
          />
        ))}

        {materiels.length > 0 && (
          <View
            style={{
              display: "flex",
              flexDirection: "column",
              marginTop: 40,
              paddingHorizontal: 40,
              flexShrink: 0,
            }}
          >
            <View style={{ width: 34, marginBottom: 10, flexShrink: 0 }}>
              <Svg
                width="34"
                height="38"
                viewBox="0 0 34 38"
                fill="none"
                style={{ flexShrink: 0 }}
              >
                <Path
                  d="M12.4004 5.2001C10.5704 5.2001 8.81536 5.92706 7.52136 7.22106C6.22735 8.51506 5.50039 10.2701 5.50039 12.1001V21.3001C5.50039 24.3501 6.712 27.2752 8.86866 29.4318C11.0253 31.5885 13.9504 32.8001 17.0004 32.8001C20.0504 32.8001 22.9755 31.5885 25.1321 29.4318C27.2888 27.2752 28.5004 24.3501 28.5004 21.3001V12.1001C28.5004 11.4901 28.7427 10.9051 29.1741 10.4738C29.6054 10.0424 30.1904 9.8001 30.8004 9.8001C31.4104 9.8001 31.9954 10.0424 32.4267 10.4738C32.8581 10.9051 33.1004 11.4901 33.1004 12.1001V21.3001C33.1004 23.4144 32.684 25.508 31.8749 27.4613C31.0658 29.4146 29.8798 31.1895 28.3848 32.6845C26.8898 34.1795 25.1149 35.3655 23.1616 36.1746C21.2083 36.9837 19.1147 37.4001 17.0004 37.4001C14.8861 37.4001 12.7925 36.9837 10.8392 36.1746C8.88585 35.3655 7.111 34.1795 5.61597 32.6845C4.12095 31.1895 2.93503 29.4146 2.12593 27.4613C1.31683 25.508 0.900391 23.4144 0.900391 21.3001V12.1001C0.900391 9.05011 2.11199 6.12504 4.26866 3.96837C6.42533 1.8117 9.3504 0.600098 12.4004 0.600098C15.4504 0.600098 18.3755 1.8117 20.5321 3.96837C22.6888 6.12504 23.9004 9.05011 23.9004 12.1001V21.3001C23.9004 23.1301 23.1734 24.8851 21.8794 26.1791C20.5854 27.4731 18.8304 28.2001 17.0004 28.2001C15.1704 28.2001 13.4154 27.4731 12.1214 26.1791C10.8274 24.8851 10.1004 23.1301 10.1004 21.3001V12.1001C10.1004 11.4901 10.3427 10.9051 10.774 10.4738C11.2054 10.0424 11.7904 9.8001 12.4004 9.8001C13.0104 9.8001 13.5954 10.0424 14.0267 10.4738C14.4581 10.9051 14.7004 11.4901 14.7004 12.1001V21.3001C14.7004 21.9101 14.9427 22.4951 15.374 22.9264C15.8054 23.3578 16.3904 23.6001 17.0004 23.6001C17.6104 23.6001 18.1954 23.3578 18.6267 22.9264C19.0581 22.4951 19.3004 21.9101 19.3004 21.3001V12.1001C19.3004 10.2701 18.5734 8.51506 17.2794 7.22106C15.9854 5.92706 14.2304 5.2001 12.4004 5.2001Z"
                  fill="#47548C"
                />
              </Svg>
            </View>
            <Text
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "#2b3674",
                marginBottom: 10,
              }}
            >
              {translate("ns1:Activities.NecessaryEquipment")}:
            </Text>
            <View style={{ display: "flex", flexDirection: "column" }}>
              {materiels?.map((material, index) => (
                <Text
                  style={{
                    fontSize: 11,
                    fontWeight: 400,
                    marginBottom: 5,
                    color: "#707eae",
                  }}
                  key={"materiel" + index}
                >
                  • {removeMarkdown(material.title)}
                </Text>
              ))}
            </View>
          </View>
        )}

        {advice && (
          <View
            style={{
              display: "flex",
              flexDirection: "column",
              marginTop: 40,
              paddingHorizontal: 40,
              flexShrink: 0,
            }}
          >
            <View style={{ marginBottom: 10, flexShrink: 0 }}>
              <Svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                style={{ flexShrink: 0 }}
              >
                <Path
                  d="M21.2996 2.9001C21.2996 2.2901 21.0573 1.70509 20.626 1.27375C20.1946 0.842418 19.6096 0.600098 18.9996 0.600098C18.3896 0.600098 17.8046 0.842418 17.3733 1.27375C16.9419 1.70509 16.6996 2.2901 16.6996 2.9001V5.2001C16.6996 5.8101 16.9419 6.39511 17.3733 6.82644C17.8046 7.25778 18.3896 7.5001 18.9996 7.5001C19.6096 7.5001 20.1946 7.25778 20.626 6.82644C21.0573 6.39511 21.2996 5.8101 21.2996 5.2001V2.9001ZM32.0107 9.2412C32.4297 8.80741 32.6615 8.22643 32.6563 7.62338C32.651 7.02032 32.4091 6.44345 31.9827 6.01701C31.5563 5.59058 30.9794 5.34869 30.3763 5.34345C29.7733 5.33821 29.1923 5.57003 28.7585 5.989L27.1324 7.6151C26.7134 8.04888 26.4816 8.62987 26.4869 9.23292C26.4921 9.83597 26.734 10.4128 27.1604 10.8393C27.5869 11.2657 28.1637 11.5076 28.7668 11.5128C29.3698 11.5181 29.9508 11.2863 30.3846 10.8673L32.0107 9.2412ZM37.3996 19.0001C37.3996 19.6101 37.1573 20.1951 36.726 20.6264C36.2946 21.0578 35.7096 21.3001 35.0996 21.3001H32.7996C32.1896 21.3001 31.6046 21.0578 31.1733 20.6264C30.7419 20.1951 30.4996 19.6101 30.4996 19.0001C30.4996 18.3901 30.7419 17.8051 31.1733 17.3738C31.6046 16.9424 32.1896 16.7001 32.7996 16.7001H35.0996C35.7096 16.7001 36.2946 16.9424 36.726 17.3738C37.1573 17.8051 37.3996 18.3901 37.3996 19.0001ZM7.61461 10.8673C7.82678 11.087 8.08057 11.2622 8.36118 11.3827C8.64179 11.5033 8.94359 11.5667 9.24899 11.5694C9.55438 11.572 9.85724 11.5138 10.1399 11.3982C10.4226 11.2825 10.6794 11.1118 10.8953 10.8958C11.1113 10.6799 11.2821 10.4231 11.3977 10.1404C11.5133 9.85773 11.5715 9.55487 11.5689 9.24948C11.5662 8.94408 11.5028 8.64228 11.3822 8.36167C11.2617 8.08106 11.0865 7.82727 10.8668 7.6151L9.24071 5.989C8.80692 5.57003 8.22594 5.33821 7.62289 5.34345C7.01983 5.34869 6.44297 5.59058 6.01653 6.01701C5.59009 6.44345 5.3482 7.02032 5.34296 7.62338C5.33772 8.22643 5.56955 8.80741 5.98851 9.2412L7.61461 10.8673ZM7.49961 19.0001C7.49961 19.6101 7.25729 20.1951 6.82595 20.6264C6.39462 21.0578 5.80961 21.3001 5.19961 21.3001H2.89961C2.28961 21.3001 1.7046 21.0578 1.27326 20.6264C0.84193 20.1951 0.599609 19.6101 0.599609 19.0001C0.599609 18.3901 0.84193 17.8051 1.27326 17.3738C1.7046 16.9424 2.28961 16.7001 2.89961 16.7001H5.19961C5.80961 16.7001 6.39462 16.9424 6.82595 17.3738C7.25729 17.8051 7.49961 18.3901 7.49961 19.0001ZM14.3996 32.8001V30.5001H23.5996V32.8001C23.5996 34.0201 23.115 35.1901 22.2523 36.0528C21.3896 36.9155 20.2196 37.4001 18.9996 37.4001C17.7796 37.4001 16.6096 36.9155 15.7469 36.0528C14.8843 35.1901 14.3996 34.0201 14.3996 32.8001ZM23.5996 28.2001C23.6341 27.4181 24.078 26.7143 24.6967 26.2244C26.1979 25.0405 27.2929 23.4183 27.8296 21.5834C28.3664 19.7484 28.3181 17.7918 27.6915 15.9856C27.065 14.1793 25.8913 12.6131 24.3335 11.5047C22.7758 10.3962 20.9115 9.80062 18.9996 9.80062C17.0878 9.80062 15.2234 10.3962 13.6657 11.5047C12.108 12.6131 10.9343 14.1793 10.3077 15.9856C9.68114 17.7918 9.63286 19.7484 10.1696 21.5834C10.7063 23.4183 11.8013 25.0405 13.3025 26.2244C13.9235 26.7143 14.3651 27.4181 14.3973 28.2001H23.6019H23.5996Z"
                  fill="#47548C"
                />
              </Svg>
            </View>
            <Text
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "#2b3674",
                marginBottom: 10,
              }}
            >
              {translate("ns1:Activities.InniziosAdvice")}:
            </Text>
            <View style={{ display: "flex", flexDirection: "column" }}>
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#707eae",
                  marginBottom: 5,
                }}
              >
                {advice.title}
              </Text>
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  color: "#707eae",
                }}
              >
                {removeMarkdown(advice.content)}
              </Text>
            </View>
          </View>
        )}
      </Page>
    </Document>
  );
};

function getBannerStyle(duration: number) {
  if (duration <= 70)
    return {
      image: grey300,
      bannerColor: "white",
    };
  else if (duration <= 90)
    return {
      image: paleGreen200,
      bannerColor: "#2b3674",
    };
  else if (duration <= 120)
    return {
      image: lightGreen500,
      bannerColor: "#2b3674",
    };
  else if (duration <= 150)
    return {
      image: paleGreen400,
      bannerColor: "white",
    };
  else if (duration <= 180)
    return {
      image: pink200,
      bannerColor: "#2b3674",
    };
  else if (duration <= 210)
    return {
      image: pink300,
      bannerColor: "#2b3674",
    };
  else if (duration <= 360)
    return {
      image: pink400,
      bannerColor: "white",
    };
  else
    return {
      image: pink500,
      bannerColor: "white",
    };
}

const InfoPill = ({
  children,
  variant = "secondary",
}: {
  children: string | never[] | JSX.Element;
  variant?: "primary" | "secondary";
}) => {
  return (
    <View
      style={{
        backgroundColor: variant === "primary" ? "#485585" : "#e1e9f8",
        padding: "5px 10px",
        borderRadius: "36px",
        marginRight: 5,
        marginTop: 5,
      }}
    >
      <Text
        style={{
          fontSize: "10px",
          fontWeight: 500,
          color: variant === "primary" ? "#fff" : "#485585",
        }}
      >
        {children}
      </Text>
    </View>
  );
};

const TechniqueComponent = ({
  technique,
  index,
  techniquesLength,
  startTime,
}: {
  technique: Technique;
  index: number;
  techniquesLength: number;
  startTime?: string;
}) => {
  return (
    <View
      style={{
        width: "100%",
        backgroundColor: "#eff4fb",
        borderRadius: 20,
        marginTop: 20,
        padding: 15,
        flexShrink: 0,
      }}
    >
      <View
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        {techniquesLength > 1 && (
          <View style={{ width: 40, marginRight: 15 }}>
            <View
              style={{
                width: 40,
                height: 40,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "100%",
                backgroundColor: "#fff",
              }}
            >
              <Text style={{ color: "#47548c", fontSize: "16px" }}>
                {index + 1}
              </Text>
            </View>
          </View>
        )}

        <View style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <View style={{ display: "flex", flexDirection: "row" }}>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: "#E1E9F8",
                paddingHorizontal: 5,
                paddingVertical: 4,
                borderRadius: 24,
                marginBottom: 7,
                flexShrink: 0,
              }}
            >
              <Svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{ marginRight: 4, flexShrink: 0 }}
              >
                <Path
                  d="M7.99998 2.66667C6.58549 2.66667 5.22894 3.22857 4.22874 4.22877C3.22855 5.22896 2.66665 6.58551 2.66665 8C2.66665 8.70039 2.8046 9.39391 3.07262 10.041C3.34065 10.6881 3.7335 11.276 4.22874 11.7712C4.72399 12.2665 5.31193 12.6593 5.959 12.9274C6.60607 13.1954 7.2996 13.3333 7.99998 13.3333C8.70036 13.3333 9.39389 13.1954 10.041 12.9274C10.688 12.6593 11.276 12.2665 11.7712 11.7712C12.2665 11.276 12.6593 10.688 12.9273 10.041C13.1954 9.39391 13.3333 8.70039 13.3333 8C13.3333 6.58551 12.7714 5.22896 11.7712 4.22877C10.771 3.22857 9.41447 2.66667 7.99998 2.66667ZM3.28593 3.28596C4.53618 2.03571 6.23187 1.33334 7.99998 1.33334C9.76809 1.33334 11.4638 2.03571 12.714 3.28596C13.9643 4.5362 14.6666 6.23189 14.6666 8C14.6666 8.87548 14.4942 9.74239 14.1592 10.5512C13.8241 11.3601 13.3331 12.095 12.714 12.714C12.095 13.3331 11.36 13.8242 10.5512 14.1592C9.74236 14.4942 8.87546 14.6667 7.99998 14.6667C7.1245 14.6667 6.25759 14.4942 5.44876 14.1592C4.63992 13.8242 3.90499 13.3331 3.28593 12.714C2.66688 12.095 2.17581 11.3601 1.84078 10.5512C1.50575 9.74239 1.33331 8.87548 1.33331 8C1.33331 6.23189 2.03569 4.5362 3.28593 3.28596ZM7.99998 4.66667C8.36817 4.66667 8.66665 4.96515 8.66665 5.33334V7.72386L10.4714 9.5286C10.7317 9.78895 10.7317 10.2111 10.4714 10.4714C10.211 10.7318 9.78892 10.7318 9.52857 10.4714L7.52857 8.47141C7.40355 8.34638 7.33331 8.17681 7.33331 8V5.33334C7.33331 4.96515 7.63179 4.66667 7.99998 4.66667Z"
                  fill="#485585"
                />
              </Svg>
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: "bold",
                  color: "#485585",
                }}
              >
                {startTime}
              </Text>
            </View>
            <View style={{ flex: 1 }}></View>
          </View>
          <Text style={{ fontWeight: "bold", color: "#2b3674", fontSize: 12 }}>
            {technique.title}
          </Text>
          <View style={{ display: "flex", flexDirection: "row" }}>
            <Text
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#47548c",
                backgroundColor: "#fff",
                padding: "2px 8px",
                borderRadius: 36,
                marginVertical: 7,
              }}
            >
              {getDurationInHours(Number(technique.duration))}
            </Text>
            <View style={{ flex: 1 }}></View>
          </View>

          <Text style={{ fontWeight: 500, color: "#2d396b", fontSize: 11 }}>
            {technique.objectif}
          </Text>
        </View>
      </View>

      {technique.sequences.map((sequence, index) => (
        <View
          style={{
            width: "100%",
            backgroundColor: "#fff",
            borderRadius: 20,
            marginTop: 20,
            padding: 15,
          }}
          key={"sequence" + index}
        >
          <View
            style={{ width: "100%", display: "flex", flexDirection: "column" }}
          >
            <Text
              style={{ fontWeight: "bold", color: "#2b3674", fontSize: 12 }}
            >
              {sequence.title}
            </Text>
            <View style={{ display: "flex", flexDirection: "row" }}>
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: "#47548c",
                  backgroundColor: "#f4f7fe",
                  padding: "2px 8px",
                  borderRadius: 36,
                  marginVertical: 7,
                }}
              >
                {getDurationInHours(Number(sequence.duration))}
              </Text>
              <View style={{ flex: 1 }}></View>
            </View>

            <Text style={{ fontSize: 11, fontWeight: 500, color: "#485585" }}>
              {removeMarkdown(sequence.storyline).replaceAll(`\\`, "")}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
};
